import { subDays } from "date-fns";
import type { PresetRange } from "./TimeControls";

const DAY_MS = 24 * 60 * 60 * 1000;

const TELEMETRY_PRESETS: Array<{ id: string; label: string; days: number }> = [
  { id: "5y", label: "5 years", days: 5 * 365 },
  { id: "year", label: "Year", days: 365 },
  { id: "month", label: "Month", days: 30 },
  { id: "week", label: "Week", days: 7 },
  { id: "day", label: "24h", days: 1 },
];

export type ClassicPresetId = "day" | "week" | "month";

const CLASSIC_PRESETS: Array<{ id: ClassicPresetId; days: number }> = [
  { id: "day", days: 1 },
  { id: "week", days: 7 },
  { id: "month", days: 30 },
];

/**
 * The standard ladder for classic (non-tiled) charts. Labels are supplied by the
 * caller rather than baked in, because consumers localize them — edges owns the
 * ids and ranges, which is the part that was duplicated per chart and drifted.
 */
export function classicPresetRanges(
  labels: Record<ClassicPresetId, string>,
  getAnchorEndMs: () => number = Date.now
): PresetRange[] {
  return CLASSIC_PRESETS.map(({ id, days }) => ({
    id,
    label: labels[id],
    getRange: () => {
      const before = new Date(getAnchorEndMs());
      // Calendar-aware, not `days * DAY_MS`: across a DST transition a "day" is
      // 23 or 25 hours. Every consumer this replaced used subDays, and several
      // still seed their default range with it.
      return { after: subDays(before, days), before };
    },
  }));
}

/**
 * The standard telemetry time-range ladder. `getAnchorEndMs` is called at press
 * time, not build time, so a long-lived view jumps to a fresh "now" (or stays
 * pinned to a scrubbed instant under time travel).
 */
export function telemetryPresetRanges(getAnchorEndMs: () => number = Date.now): PresetRange[] {
  return TELEMETRY_PRESETS.map(({ id, label, days }) => ({
    id,
    label,
    getRange: () => {
      const endMs = getAnchorEndMs();
      return { after: new Date(endMs - days * DAY_MS), before: new Date(endMs) };
    },
  }));
}
